/**
 * AvatarPreview - Renders the selected character sprite (idle, front-facing) with hue tint.
 */
import React, { useRef, useEffect } from 'react';
import { ensureSpriteLoaded, drawPlayerSprite, computeTint, resolveGender } from './playerSprite';
import { drawOutfitLayers } from './outfitLayers';

const AvatarPreview = ({ avatar, isTurtle }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    // Preload the selected character
    const gender = resolveGender(avatar?.gender);
    ensureSpriteLoaded(gender);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // Subtle radial spotlight background
      const bgGrad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.7);
      bgGrad.addColorStop(0, 'rgba(88,28,135,0.18)');
      bgGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, W, H);

      // Floor disc shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
      ctx.beginPath();
      ctx.ellipse(W / 2, H - 18, 38, 8, 0, 0, Math.PI * 2);
      ctx.fill();

      const isSheetSpec = isSheetSpecial(avatar?.special);
      if (isTurtle || isSheetSpec) {
        drawSpecialSprite(ctx, isSheetSpec ? avatar.special : 'special_turtle_man', W / 2, H / 2 + 20, 1, 'down');
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      // Combined hue: spriteHue + skin-tone offset (shared with in-club renderer)
      const tint = computeTint(avatar);

      // Strut in place so the outfit's walk animation shows off (walk 2.4s, rest 1.6s)
      const cycle = (Date.now() % 4000) / 4000;
      const strut = cycle < 0.6;
      const ph = Date.now() * 0.014;

      // Idle breathing offset for life
      const breathe = strut ? 0 : Math.sin(Date.now() * 0.0015) * 1.5;
      const cx = W / 2;
      const cy = H / 2 + 30 + breathe;

      // Render the selected character sprite (down-facing, scale ~2.2 for preview)
      drawPlayerSprite(ctx, cx, cy, 'down', strut, ph, 2.2, tint, gender);
      drawOutfitLayers(ctx, avatar, cx, cy, 'down', strut, ph, 2.2);

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [avatar, isTurtle]);

  return (
    <canvas
      ref={canvasRef}
      width={220}
      height={260}
      className="block"
      style={{ imageRendering: 'pixelated' }}
      data-testid="avatar-preview-canvas"
    />
  );
};

// Turtle sprite + all sheet specials now live in specialSprites.js
import { drawSpecialSprite, isSheetSpecial } from './specialSprites';
export { drawSpecialSprite, isSheetSpecial };

// Back-compat wrapper (Nightclub.js and thumbs import drawTurtle from here)
export function drawTurtle(ctx, x, y, scale = 1, direction = 'down', isWalking = false, walkPhase = 0) {
  drawSpecialSprite(ctx, 'special_turtle_man', x, y, scale, direction, isWalking, walkPhase);
}

export default AvatarPreview;
